/**
 * Zoho Books invoice status labels and badge tones.
 * Kept separate from CRM schedule status (planned / issued / paid / …).
 */

const ALIASES = {
    'pending approval': 'pending_approval',
    pendingapproval: 'pending_approval',
    accepted: 'approved',
    'partially paid': 'partially_paid',
    partiallypaid: 'partially_paid',
};

const LABELS = {
    draft: 'Draft',
    pending_approval: 'Pending Approval',
    approved: 'Accepted',
    sent: 'Sent',
    partially_paid: 'Partially Paid',
    paid: 'Paid',
    rejected: 'Rejected',
    overdue: 'Overdue',
    unpaid: 'Unpaid',
    viewed: 'Viewed',
    void: 'Void',
};

const TONES = {
    draft: 'grey',
    pending_approval: 'grey',
    approved: 'blue',
    sent: 'blue',
    partially_paid: 'amber',
    paid: 'green',
    rejected: 'red',
    overdue: 'red',
    unpaid: 'amber',
    viewed: 'blue',
    void: 'grey',
};

const TONE_TO_VARIANT = {
    grey: 'secondary',
    blue: 'info',
    amber: 'warning',
    green: 'success',
    red: 'destructive',
};

export function normalizeZohoStatus(raw) {
    if (raw == null) return null;
    const text = String(raw).trim();
    if (!text) return null;
    const key = text.toLowerCase().replace(/-/g, '_').replace(/_/g, ' ').trim();
    const collapsed = key.replace(/\s+/g, '');
    if (ALIASES[key]) return ALIASES[key];
    if (ALIASES[collapsed]) return ALIASES[collapsed];
    return text.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
}

export function zohoStatusLabel(raw) {
    const status = normalizeZohoStatus(raw);
    if (!status) return '';
    return LABELS[status] || status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function zohoStatusBadgeTone(raw) {
    const status = normalizeZohoStatus(raw);
    if (!status) return 'grey';
    return TONES[status] || 'grey';
}

export function zohoStatusBadgeVariant(raw) {
    return TONE_TO_VARIANT[zohoStatusBadgeTone(raw)] || 'secondary';
}

export function formatZohoSyncedAgo(iso) {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
    if (mins < 1) return 'synced just now';
    if (mins < 60) return `synced ${mins}m ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `synced ${hours}h ago`;
    const days = Math.round(hours / 24);
    return `synced ${days}d ago`;
}

export function zohoStatusDetail(schedule, formatMoney) {
    const status = normalizeZohoStatus(schedule?.zohoStatus);
    if (!status) return '';
    if (status === 'overdue') {
        const days = schedule.overdueDays;
        if (days == null || days === '') return '';
        return `Overdue by ${days} days`;
    }
    if (status === 'partially_paid') {
        const money = typeof formatMoney === 'function' ? formatMoney : (v) => String(v ?? '');
        return `Received ${money(schedule.zohoAmountPaid)} of ${money(schedule.zohoTotal)} — Balance ${money(schedule.zohoBalanceDue)}`;
    }
    if (status === 'paid') {
        if (schedule.zohoAmountPaid == null || schedule.zohoAmountPaid === '') {
            if (schedule.zohoTotal != null && schedule.zohoTotal !== '') {
                const money = typeof formatMoney === 'function' ? formatMoney : (v) => String(v ?? '');
                return `Paid in full · ${money(schedule.zohoTotal)}`;
            }
        }
        return 'Paid in full';
    }
    if (status === 'sent') return 'Awaiting payment';
    return '';
}
